import { onScreen } from './visible';
import { inputOf } from './anchors';
import { bridge } from './bridge';
import { bestMatch, similarity } from './hangul';
import { OptionNotFound, setValue } from './input';

// 모달이 뜨고 표가 그려지기를 기다리는 시간.
const OPEN_MS = 5000;
/** 검색칸을 비우고 다시 부른 목록이 오기까지 한 번 더 기다리는 시간. */
const REFILL_MS = 1500;
const POLL_MS = 50;
/** 행 클릭 뒤 포커스가 앉기까지. `TableLayout` 은 300ms 안의 두 번째 클릭을 버린다. */
const ROW_FOCUS_MS = 350;
/** 둘째 클릭의 포커스 알림(100ms 지연)이 화면 상태에 닿기까지. */
const ROW_SETTLE_MS = 200;
/**
 * 검색어를 친 뒤 목록이 **갈리기를** 기다리는 시간.
 *
 * 서버가 답을 다 돌려주기까지가 아니라 **표가 반응하기까지**의 유예다. 목록이 비는
 * 순간 지문이 갈리고, 그때부터는 `waitRows` 가 `SEARCH_MS` 를 들고 기다린다.
 *
 * `SEARCH_MS` 를 쓰면 안 된다. 검색칸이 코드로만 거르는 화면은 아무리 기다려도 목록이
 * 그대로라 검색어마다 이 시간을 통째로 버리고, 한 칸이 여러 검색어를 시도하므로
 * 8초짜리 시한은 칸 하나가 한 턴 예산(120초)의 삼분의 일을 먹는다.
 */
const REFRESH_MS = 500;
// 검색어를 넣고 서버가 목록을 돌려주기까지.
const SEARCH_MS = 8000;
/**
 * 「이 목록은 통째로 보여줄 수 있다」의 경계. 행이 이보다 많으면 앞 몇 줄은 목록이
 * 아니라 **임의의 조각**이다.
 *
 * 20 인 이유는 `names()` 가 되묻기에 내미는 상한이 20 이기 때문이다 — 그 위로는
 * 무엇을 내밀어도 잘라 낸 것이라 「전부」가 아니다. 두 잣대가 같은 수여야 「보여줄 수
 * 있으면 보여준다」가 성립한다. 실제로 걸리는 목록도 이 아래에 있다: 호기 3건
 * (`actions.test.ts` 의 `1호기`), 고장부위 2건(같은 파일의 팝업 탐침).
 */
const WHOLE_LIST_MAX = 20;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

// 「지금 화면에 있는가」는 `visible.ts` 하나가 정한다. 닫힌 모달은 상자를 안 만든다.
const visible = onScreen;

/**
 * 지금 화면에 떠 있는 공용 검색 모달.
 *
 * **뿌리는 모달뿐이다.** 「검색어」 글자만으로는 안 된다 — 고장접수 같은 화면은
 * 자기 조회조건에 검색어 칸을 갖고 있고(카탈로그 `searchQuery`), 예전에는 그 줄에서
 * 네 단계를 올라가 **화면의 표**를 뿌리로 잡았다. 팝업이 안 열린 턴에 접수일자·접수번호
 * (`20260903` `001`)를 후보라고 내민 것이 그것이다. 모달이 없으면 없는 것이다.
 */
function popupRoot(exclude?: HTMLElement): HTMLElement | null {
  const labels = Array.from(document.querySelectorAll<HTMLElement>('span,label,div'))
    .filter((element) => element.children.length === 0
      && (element.textContent ?? '').trim().startsWith('검색어'));
  for (const label of labels.reverse()) {
    let wrapper: HTMLElement | null = label;
    for (let depth = 0; depth < 6 && wrapper; depth += 1) {
      if (wrapper.querySelector('input')) break;
      wrapper = wrapper.parentElement;
    }
    if (!wrapper) continue;
    // 검색어 칸을 품은 줄이 아니라 **모달 전체**를 돌려줘야 결과 표까지 보인다.
    const modal = wrapper.closest<HTMLElement>('[class*=Modal], [class*=modal]');
    if (!modal) continue;
    const node = modal;
    // 닫힌 모달은 지워지지 않고 **숨겨진 채 남는다.** 그것을 열려 있다고 보면
    // 화면이 이미 값을 확정했는데도 표를 찾다가 실패로 뒤집는다.
    if (!visible(node)) continue;
    // 화면 자체의 조회조건에도 `검색어` 칸이 있다. 지금 채우려는 칸을 품은 컨테이너는
    // 팝업이 아니라 그 화면이다.
    if (exclude && node.contains(exclude)) continue;
    return node;
  }
  return null;
}

/** 앱이 서버를 기다리는 중인가. 구버전 앱·시험 DOM 에는 이 통로가 없다. */
function busyProbe(): (() => boolean) | undefined {
  try {
    return bridge().busy;
  } catch {
    return undefined;
  }
}

/**
 * 팝업을 열고 그 뿌리를 잡는다. **바쁜 화면에 던진 더블클릭은 씹힌다.**
 *
 * 씹힌 것을 「후보가 이것뿐」으로 읽지 않으려면 두 가지가 필요하다 — 바쁨이 걷힌 뒤에
 * 열고, 그래도 안 뜨면 한 번 더 여는 것. 두 번 다 실패하면 뿌리는 없다.
 *
 * **한 번의 시도가 쓰는 예산은 `openMs` 하나다.** 바쁨을 기다리는 시간과 뿌리를 찾는
 * 시간이 같은 시한을 나눠 쓴다. 따로 세면 한 칸이 `openMs` 의 네 배를 먹고, 되묻기까지
 * 가면 한 턴 예산(120초)의 삼분의 일이 칸 하나에 들어간다.
 */
async function openRoot(
  field: HTMLInputElement | HTMLTextAreaElement | null,
  open: () => void,
  openMs: number,
  done: () => boolean = () => false,
): Promise<HTMLElement | null> {
  const busy = busyProbe();
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const deadline = Date.now() + openMs;
    // 바쁜 화면에 던진 더블클릭은 씹힌다. 걷힌 뒤에 연다.
    // eslint-disable-next-line no-await-in-loop
    while (busy && busy() && Date.now() < deadline) await sleep(POLL_MS);
    if (done()) return null;
    open();
    let root = popupRoot(field ?? undefined);
    while (!root && Date.now() < deadline) {
      if (done()) return null;
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
      root = popupRoot(field ?? undefined);
    }
    if (root) return root;
  }
  return null;
}

/** 팝업이 끝내 안 열렸다. 후보를 지어내지 않고 **왜** 비었는지를 들고 던진다. */
function popupNeverOpened(wanted: string): OptionNotFound {
  const error = new OptionNotFound(wanted, []);
  error.reason = '팝업이 안 열렸다';
  return error;
}

/** 요소 아래 잎들의 글자. 표시 순서 그대로. */
function leafTexts(node: HTMLElement): string[] {
  return Array.from(node.querySelectorAll<HTMLElement>('*'))
    .filter((element) => element.children.length === 0)
    .map((element) => (element.textContent ?? '').trim())
    .filter(Boolean);
}

function isCode(text: string): boolean {
  return /^[0-9A-Za-z_-]{1,20}$/.test(text);
}

/**
 * 그 셀이 속한 결과 행.
 *
 * 표는 셀을 여러 겹으로 감싼다(행 > 셀 래퍼 > 글자). 셀의 첫 부모를 행으로 보면
 * 글자 하나짜리 래퍼가 잡힌다. **코드꼴 글자와 이름을 함께 가진** 가장 가까운
 * 조상이 행이다.
 */
function rowOf(cell: HTMLElement, root: HTMLElement): HTMLElement | null {
  let node = cell.parentElement;
  for (let depth = 0; depth < 5 && node && node !== root; depth += 1) {
    const texts = leafTexts(node);
    if (texts.length >= 2 && isCode(texts[0] ?? '') && Boolean(texts[1])) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * 모달 안의 결과 행들.
 *
 * 화면이 행 번호를 내보내면(`data-el-row`) 그것이 정답이다. 표 컴포넌트가 여럿이라
 * (`GridLayout`·`TableLayout`) 모양 추측은 한쪽에서만 맞는다 — 실제로 품목 모달의
 * 행을 못 읽어 "찾은 것이 없습니다" 로 끝났다.
 */
function rowsOf(root: HTMLElement, query: HTMLInputElement | null): HTMLElement[] {
  const marked = Array.from(root.querySelectorAll<HTMLElement>('[data-el-row]'))
    .filter((row) => !row.querySelector('input'))
    .filter((row) => leafTexts(row).length > 0);
  if (marked.length) return marked;
  // **앱이 표를 표시해 둔 창이면 짐작하지 않는다.** 표가 아직 비었을 뿐이다 — 매입등록의
  // 거래처 창은 1만 줄을 받느라 1.5초 넘게 비어 있고, 그동안 합계 줄의 「0」을 행으로 짐작해
  // 눌러 거래처가 빈 채로 창이 닫혔다(2026-09-25). 빈 배열이면 부르는 쪽이 기다린다.
  if (root.querySelector('[data-el-grid]')) return [];
  return rowsByShape(root, query);
}

/**
 * 목록을 굴리는 상자.
 *
 * 가상 스크롤 표는 보이는 행만 DOM 에 두므로, 이 상자의 위치가 곧 「지금 무엇이
 * 보이는가」다. 훑기(`scanRows`)와 지문 재기가 **같은 상자**를 봐야 두 자리가 어긋나지
 * 않는다 — 각자 찾으면 한쪽만 되감고 다른 쪽은 딴 창을 재는 일이 생긴다.
 */
export function scrollerOf(root: HTMLElement): HTMLElement | null {
  return root.querySelector<HTMLElement>('[class*=ReactVirtualized__Grid]')
    ?? Array.from(root.querySelectorAll<HTMLElement>('*'))
      .find((element) => element.scrollHeight > element.clientHeight + 8)
    ?? null;
}

/**
 * 목록을 위에서 아래까지 훑으며 행을 모은다.
 *
 * 이 팝업의 표는 가상 스크롤이라 **화면에 보이는 행만 DOM 에 있다.** 스무 개를 후보로
 * 내밀어도 그것은 지금 보이는 창일 뿐이고, 다음에 열면 다른 창이 보인다 — 사용자가 고른
 * `주식회사 광주화교협회` 가 그다음 턴의 DOM 에는 없어서 영원히 되묻던 이유다.
 * 찾으면 거기서 멈춘다. 끝까지 없으면 훑는 동안 본 행을 모두 돌려준다.
 */
async function scanRows(
  root: HTMLElement,
  query: HTMLInputElement | null,
  match: (rows: HTMLElement[]) => HTMLElement | undefined,
): Promise<{ chosen?: HTMLElement; seen: HTMLElement[] }> {
  const seen = new Map<string, HTMLElement>();
  const collect = (): HTMLElement | undefined => {
    const rows = rowsOf(root, query);
    for (const row of rows) seen.set(textOf(row), row);
    return match(rows);
  };
  let chosen = collect();
  if (chosen) return { chosen, seen: Array.from(seen.values()) };

  const scroller = scrollerOf(root);
  if (!scroller) return { seen: Array.from(seen.values()) };

  const step = Math.max(scroller.clientHeight - 40, 80);
  for (let top = step; top < scroller.scrollHeight && seen.size < 400; top += step) {
    scroller.scrollTop = top;
    scroller.dispatchEvent(new Event('scroll', { bubbles: true }));
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    chosen = collect();
    if (chosen) return { chosen, seen: Array.from(seen.values()) };
  }
  return { seen: Array.from(seen.values()) };
}

/** 표식이 없는 옛 화면용. 코드꼴 셀과 이름을 함께 가진 조상을 행으로 본다. */
function rowsByShape(root: HTMLElement, query: HTMLInputElement | null): HTMLElement[] {
  const rows: HTMLElement[] = [];
  const cells = Array.from(root.querySelectorAll<HTMLElement>('*'))
    .filter((element) => element.children.length === 0)
    .filter((element) => isCode((element.textContent ?? '').trim()));
  for (const cell of cells) {
    const row = rowOf(cell, root);
    if (!row || row.querySelector('input') || row === query?.parentElement) continue;
    if (!rows.some((other) => other === row || other.contains(row) || row.contains(other))) rows.push(row);
  }
  return rows;
}

/**
 * 행의 글자. 셀 사이를 한 칸으로 잇는다.
 *
 * `textContent` 를 그냥 쓰면 셀이 붙어 `A1광주현장` 이 된다 — 코드와 이름을 가를 수 없다.
 */
function textOf(row: HTMLElement): string {
  return leafTexts(row).join(' ').trim().replace(/\s+/g, ' ');
}

function normalized(value: string): string {
  return value.replace(/[^0-9A-Za-z가-힣]/g, '').toLocaleLowerCase();
}

/** 코드를 담는 칸 이름. 이 앱은 `actcd`·`pcode`·`phm_pcod` 처럼 쓴다. */
const CODE_COLUMN = /(cd|cod|id|code)$/i;
/** 이름을 담는 칸. `cdnm`·`pname`·`phm_pnam` 셋 다 쓰인다. */
const NAME_COLUMN = /(nm|nam|name)$/i;

/** 행이 스스로 밝힌 칸들. 컬럼 이름 → 글자. 표가 표식을 낼 때만 채워진다. */
function cellsOf(row: HTMLElement): Map<string, string> {
  const cells = new Map<string, string>();
  for (const cell of Array.from(row.querySelectorAll<HTMLElement>('[data-el-cell]'))) {
    const id = cell.getAttribute('data-el-cell') ?? '';
    if (!id || cells.has(id)) continue;
    cells.set(id, (cell.textContent ?? '').trim());
  }
  return cells;
}

/**
 * 행 글자는 `코드 코드명` 이다. 사람에게 보여 줄 이름만 남긴다.
 *
 * 다만 그 모양은 칸이 둘일 때만 맞는다. 품목 모달의 한 행은
 * `자사 X 3101010009 WIRE ROPE 8mm X 5R m 0 0 0` 이라 코드가 셋째 칸이고, 앞 칸이
 * 코드꼴이 아니라 **행 전체가 이름이 됐다.** 그러면 `WIRE ROPE` 를 물어도 어느 행과도
 * 정확히 같지 않아 다섯 개가 동점으로 남고 언제나 되묻는다.
 *
 * 표가 컬럼 이름을 내보내면(`data-el-cell`) 그것이 정답이다. 이름 칸이 여럿인 표가
 * 있으므로(`phm_pnam` 품목명 옆에 `phm_knam` 약명, 뒤에 그룹명들) **코드 칸 바로 뒤**를
 * 그 행의 정체로 본다 — 이 앱은 언제나 코드와 이름을 나란히 둔다(`cd`→`cdnm`,
 * `phm_pcod`→`phm_pnam`).
 */
function nameOf(row: HTMLElement): string {
  const cells = Array.from(cellsOf(row));
  const named = ([id, text]: [string, string]): boolean => NAME_COLUMN.test(id) && Boolean(text);
  for (let index = 0; index < cells.length; index += 1) {
    const next = cells[index + 1];
    if (CODE_COLUMN.test(cells[index]?.[0] ?? '') && next && named(next)) return next[1];
  }
  const first = cells.find(named);
  if (first) return first[1];
  const text = textOf(row);
  const [code, ...rest] = text.split(' ');
  return rest.length && /^[0-9A-Za-z_-]+$/.test(code ?? '') ? rest.join(' ') : text;
}

/**
 * 그 행의 코드 칸 값(pcode·actcd 등). `nameOf` 와 **같은 짝**을 본다 — 코드 칸 바로 뒤에
 * 이름 칸이 붙는 자리가 그 행의 정체다. 품목 모달은 앞에 `wkactcd`(자사) 같은 다른 코드
 * 칸이 있어(`cd` 로 끝난다) 그냥 첫 코드 칸을 집으면 `자사` 가 나온다. 없으면 ''.
 */
function codeOf(row: HTMLElement): string {
  const cells = Array.from(cellsOf(row));
  const named = ([id, text]: [string, string]): boolean => NAME_COLUMN.test(id) && Boolean(text);
  for (let index = 0; index < cells.length; index += 1) {
    const cell = cells[index];
    const next = cells[index + 1];
    if (cell && cell[1] && CODE_COLUMN.test(cell[0]) && next && named(next)) return cell[1];
  }
  const hit = cells.find(([id, text]) => CODE_COLUMN.test(id) && Boolean(text));
  return hit ? hit[1] : '';
}

/**
 * 되묻기에 내밀 이름. **이름이 같은 행이 있으면 가르는 칸을 덧붙인다.**
 *
 * 개발 DB 의 `WIRE ROPE` 는 다섯 줄이고 규격만 다르다(`로프 일대일, 4본`,
 * `로프 이대일, 6본 12mm`…). 이름만 내밀면 똑같은 선택지 다섯 개가 되고, 사용자가
 * 무엇을 고르든 다음 턴에서 또 동점이라 영원히 되묻는다.
 *
 * 고르는 잣대도 이 이름을 쓴다. 사용자가 답으로 돌려준 것이 곧 이 글자이기 때문이다.
 */
function labelOf(row: HTMLElement, all: HTMLElement[]): string {
  const name = nameOf(row);
  const twins = all.filter((other) => other !== row && nameOf(other) === name);
  if (!twins.length) return name;
  const others = twins.map(cellsOf);
  const mine = Array.from(cellsOf(row))
    .filter(([id, text]) => text && !NAME_COLUMN.test(id))
    .filter(([id, text]) => others.every((cells) => cells.get(id) !== text));
  // 코드는 가르기는 해도 사람에게 아무 말도 하지 않는다. `WIRE ROPE 3101010056` 보다
  // `WIRE ROPE 로프 일대일, 4본` 이 고를 수 있는 글자다. 가를 것이 코드뿐이면 그때 쓴다.
  const spoken = mine.find(([id]) => !CODE_COLUMN.test(id)) ?? mine[0];
  return spoken ? `${name} ${spoken[1]}` : name;
}

/**
 * 지금 화면이 이미 쓰고 있는 값들.
 *
 * 이름만 보면 `광주 현장` 은 스무 곳에 걸린다. 그 화면의 기존 문서들이 실제로 쓴
 * 현장·담당자가 곧 맥락이다 — 부품·자재는 같은 곳에서 반복해서 나간다.
 */
function contextValues(): Set<string> {
  const seen = new Set<string>();
  let state: Record<string, unknown> | null = null;
  try {
    state = bridge().pageState();
  } catch {
    return seen;
  }
  for (const value of Object.values(state ?? {})) {
    if (!Array.isArray(value)) continue;
    for (const row of value.slice(0, 200)) {
      if (!row || typeof row !== 'object') continue;
      for (const cell of Object.values(row as Record<string, unknown>)) {
        if (typeof cell === 'string' && cell.trim()) seen.add(normalized(cell));
      }
    }
  }
  return seen;
}

/**
 * 후보 중 하나를 고른다. **확신이 있을 때만** 고르고, 아니면 부르는 쪽이 사람에게 묻는다.
 *
 * 순서: 이름이 정확히 같은 것 → 부분 일치가 하나뿐 → 그중 화면 이력에 있는 것이
 * 하나뿐 → 후보 자체가 하나뿐.
 */
function pickRow(rows: HTMLElement[], wanted: string): HTMLElement | undefined {
  // 이름이 똑같은 행이 둘이면 첫 번째를 집으면 안 된다. 규격만 다른 자재가 그렇다 —
  // 어느 쪽인지는 화면을 보는 사람만 안다.
  const exact = rows.filter((row) => normalized(labelOf(row, rows)) === normalized(wanted));
  if (exact.length === 1) return exact[0];
  if (exact.length > 1) return undefined;
  // 점수와 **마진**을 함께 본다. 1위가 뚜렷하지 않으면 고르지 않는다 — 금호빌딩이
  // 두 곳인 계정에서 아무 쪽이나 확정하면 다른 현장에 문서가 붙는다.
  const scored = bestMatch(wanted, rows, (row) => labelOf(row, rows));
  if (scored.chosen) return scored.chosen;
  // 비슷한 것이 여럿일 때, 그 화면이 이미 쓰고 있는 값이 하나뿐이면 그것이 맥락이다.
  const known = contextValues();
  const seen = scored.ranked
    .filter((entry) => entry.score >= 0.5)
    .map((entry) => entry.candidate)
    .filter((row) => known.has(normalized(nameOf(row))));
  if (seen.length === 1) return seen[0];
  // **하나뿐이어도 말한 것과 닮아야 고른다.** 매입 거래처 창에 「더 브라운 캐슬」을 찾았더니
  // 창의 기본 거름에 그 거래처가 없었고, 「브라운」으로 좁힌 결과 한 줄 `브라운백커피(주)`
  // (닮음 0.21)가 확정돼 **남의 거래처로 저장됐다**(2026-09-25, 개발 테넌트). 안 닮았으면
  // 고르지 않는다 — 부르는 쪽이 후보를 내밀어 묻는다.
  const [only] = rows;
  if (rows.length !== 1 || !only) return undefined;
  return similarity(wanted, labelOf(only, rows)) >= 0.5
    || normalized(textOf(only)).includes(normalized(wanted)) ? only : undefined;
}

/**
 * 좁혀 가며 넣어 볼 검색어들. 구체적인 것부터 둔다.
 *
 * 되묻기로 받은 답은 우리가 내민 라벨 그대로다 — `주식회사 광주화교협회[7002-983]` 처럼
 * 코드와 별칭이 붙어 있다. 그 문자열은 검색에 걸리지 않고, 첫 어절 `주식회사` 는 수백 건에
 * 걸려 목표가 첫 페이지 밖으로 밀린다. 괄호 안 코드와 가장 긴 낱말을 사이에 끼운다.
 */
export function queriesFor(wanted: string): string[] {
  const bare = wanted.replace(/[[(][^\])]*[\])]/g, ' ').replace(/\s+/g, ' ').trim();
  // 첫 괄호가 아니라 가장 긴 괄호를 쓴다 — `(주)화순컨트리클럽[7014-203]` 의 첫 괄호는 `주` 다.
  const bracketed = Array.from(wanted.matchAll(/[[(]([^\])]+)[\])]/g))
    .map((hit) => (hit[1] ?? '').trim())
    .sort((a, b) => b.length - a.length)[0] ?? '';
  const words = bare.split(/\s+/).filter(Boolean);
  const longest = words.slice().sort((a, b) => b.length - a.length)[0] ?? '';
  const head = words[0] ?? bare;
  // **붙어 있는 영문과 한글을 쪼갠다.** 사람은 `LED조명` 이라 말하지만 품목 이름은
  // `LED등기구`·`LED램프`·`LED LIGHT` 다. 통째로도 앞 네 자(`LED조`)로도 0건이라
  // 「품번 목록에서 찾지 못했습니다」로 끝났다 — 사람이라면 팝업에 `LED` 만 친다
  // (2026-09-16 실측, 이 테넌트에 LED 든 품목이 78개다).
  //
  // 글자 갈래(영문·한글·숫자)가 바뀌는 자리에서 자른다. 한 갈래로만 된 말은 그대로라
  // 지금까지 통하던 이름들의 검색어는 하나도 안 바뀐다.
  const pieces = words.flatMap((word) => word.match(/[A-Za-z]+|[가-힣]+|\d+/g) ?? [])
    .filter((piece) => piece.length >= 2 && piece !== bare && !words.includes(piece));
  return Array.from(new Set([wanted, bare, bracketed, longest, head, ...pieces, head.slice(0, 4)]))
    .filter((q) => q.length >= 2);
}

/**
 * 좌표를 실은 마우스 이벤트.
 *
 * 좌표 없이 보내면 `clientX/clientY` 가 0 이다. 팝업 표는 가상 스크롤이라 그 좌표로
 * 몇 번째 행인지 되계산하고, 0,0 은 언제나 **첫 행**이다 — 덕봉빌딩을 눌렀는데
 * `00001 1번입니다` 가 확정되는 것을 봤다. 진짜 클릭은 되고 합성 클릭만 틀렸던 이유다.
 */
/** 되묻기에 내밀 이름들. 스무 개면 충분하다. */
function names(rows: HTMLElement[]): string[] {
  return rows.slice(0, 20).map((row) => labelOf(row, rows));
}

/**
 * 행을 고른다 — 누르고, **포커스가 앉기를 기다렸다가** 더블클릭한다.
 *
 * `TableLayout` 의 행 클릭은 300ms 안에 연달아 오면 무시되고, 포커스 알림은 100ms 뒤에
 * 간다. 매입등록의 거래처 창은 더블클릭이 **지금 포커스된 행**을 확정하는데, 누르자마자
 * 더블클릭하면 창이 조회 뒤 스스로 잡은 첫 행이 확정됐다(2026-09-25, 00001). 그래서 한 번
 * 누르고 기다렸다가 다시 누른다 — 첫 클릭이 무시됐어도 둘째가 잡는다.
 */
async function chooseRow(row: HTMLElement, root?: HTMLElement): Promise<() => Promise<HTMLElement>> {
  const live = liveRow(row, root);
  press(await live(), 'mousedown');
  press(await live(), 'mouseup');
  press(await live(), 'click');
  await sleep(ROW_FOCUS_MS);
  press(await live(), 'click');
  await sleep(ROW_SETTLE_MS);
  press(await live(), 'dblclick', 2);
  return live;
}

/**
 * 고른 행을 **지금 DOM 에서** 다시 찾는 함수를 돌려준다.
 *
 * 누르면 표가 그 행을 **다시 그린다**(`preRenderData[index] = undefined`). 들고 있던 요소는
 * DOM 에서 떨어져 나가 뒤의 클릭이 허공에 간다 — 그래서 매번 같은 행 번호로 다시 찾는다.
 *
 * 찾는 범위는 **모달 전체**다(2026-09-25). 공용 검색 창(`ModalCommon` 의 `GridLayout`)은
 * `data-el-grid` 를 안 내서 예전에는 행의 부모 안에서만 찾았는데, 그 부모째 갈려 나가면
 * 떨어진 행을 그대로 돌려줬다. 떨어진 요소에 쏜 더블클릭은 아무 데도 안 닿고, 부르는 쪽은
 * 「확인」을 눌러 앱이 스스로 포커스한 **첫 행**(`0101 직원급여`)을 확정했다.
 *
 * 앱이 목록을 다 받은 뒤 0.5초 있다가 첫 행으로 **되감는다**(`GridLayout.setFocus(0)` 의
 * `scrollToRow(0)`). 깊이 굴려서 찾은 행은 그 순간 DOM 에서 사라지므로, 없으면 찾았던
 * 자리로 다시 굴린다. 행 번호만 보지 않고 글자도 견준다 — 목록이 새로 왔으면 같은 번호가
 * 다른 행이다.
 */
function liveRow(row: HTMLElement, root?: HTMLElement): () => Promise<HTMLElement> {
  const key = row.getAttribute('data-el-row');
  const text = textOf(row);
  const scope = (): HTMLElement | null => (root?.isConnected ? root : null)
    ?? row.closest<HTMLElement>('[data-el-grid]')
    ?? (row.parentElement?.isConnected ? row.parentElement : null);
  const top = root ? (scrollerOf(root)?.scrollTop ?? 0) : 0;
  const find = (): HTMLElement | undefined => {
    const where = scope();
    if (!where) return undefined;
    return Array.from(where.querySelectorAll<HTMLElement>('[data-el-row]'))
      .find((one) => one.getAttribute('data-el-row') === key && textOf(one) === text);
  };
  return async () => {
    if (row.isConnected || key === null) return row;
    const found = find();
    if (found) return found;
    const scroller = root?.isConnected ? scrollerOf(root) : null;
    if (!scroller) return row;
    scroller.scrollTop = top;
    scroller.dispatchEvent(new Event('scroll', { bubbles: true }));
    await sleep(POLL_MS);
    return find() ?? row;
  };
}

function press(element: HTMLElement, type: string, detail = 1): void {
  const box = element.getBoundingClientRect();
  const x = box.left + box.width / 2;
  const y = box.top + box.height / 2;
  element.dispatchEvent(new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    detail,
    clientX: x,
    clientY: y,
    screenX: x,
    screenY: y,
  }));
}

/**
 * 검색 팝업이 걸린 칸(현장명·담당자 등)을 사람처럼 채운다.
 *
 * 이런 칸은 이름을 쳐 넣어도 화면 상태에 남지 않는다. 값은 팝업에서 고른 행이
 * 코드와 이름을 **함께** 넣어 줄 때만 들어간다. 그래서 더블클릭으로 팝업을 열고,
 * 검색어를 넣고, 이름이 맞는 행을 더블클릭한다.
 *
 * 후보가 여럿이면 고르지 않고 `OptionNotFound` 로 이름들을 돌려준다 — 부르는 쪽이
 * 사용자에게 그대로 물을 수 있어야 한다.
 */
export async function pickFromSearchPopup(
  anchor: HTMLElement,
  wanted: string,
  openMs = OPEN_MS,
): Promise<string[]> {
  const field = inputOf(anchor);
  if (!field) throw new Error('검색 팝업을 열 입력칸이 없다');
  // 여는 동작 **전에** 값을 기억해야 한다. 팝업이 바로 확정해 버리는 화면이 있어,
  // 연 뒤에 읽으면 "안 바뀌었다" 로 보이고 영영 기다린다.
  const before = field.value;
  const done = () => field.value !== before && Boolean(field.value.trim());
  const root = await openRoot(field, () => press(field, 'dblclick'), openMs, done);
  // 값이 이미 들어왔으면 성공이다 — 검색 결과가 1건이면 앱이 스스로 확정하고 닫는다.
  if (!root) {
    if (done()) return [];
    throw popupNeverOpened(wanted);
  }
  return pickInOpenPopup(field, wanted, openMs, before);
}

/**
 * **이미 열린** 검색 모달에서 고른다.
 *
 * 여는 방법은 칸마다 다르다 — 헤더 칸은 더블클릭(`bindSearchModal`), 표의 품번 칸은
 * 값을 치고 Enter(`onEnter` 가 품목 모달을 연다). 고르는 절차는 같다.
 */
export async function pickInOpenPopup(
  field: HTMLInputElement | HTMLTextAreaElement,
  wanted: string,
  openMs = OPEN_MS,
  valueBefore?: string,
): Promise<string[]> {
  // 값이 채워졌는지가 유일하게 믿을 만한 성공 신호다. 모달이 열렸다 닫혔는지,
  // DOM 에 남았는지는 화면마다 다르다 — 검색 결과가 1건이면 앱이 스스로 확정한다.
  const before = valueBefore ?? field.value;
  const done = () => field.value !== before && Boolean(field.value.trim());

  const deadline = Date.now() + openMs;
  let root = popupRoot(field);
  while (!root && Date.now() < deadline) {
    if (done()) return [];
    await sleep(POLL_MS);
    root = popupRoot(field);
  }
  if (!root) {
    if (done()) return [];
    closePopup(popupRoot(field));
    throw new Error(`검색 팝업이 열리지 않았다: ${wanted}`);
  }

  const query = root.querySelector<HTMLInputElement>('input');

  // 검색 결과가 하나뿐이면 화면이 스스로 확정하고 팝업을 닫는다. 그때 값은 이미
  // 들어가 있다 — 계속 표를 찾다가 "고르지 못했다" 로 뒤집으면 안 된다.
  // 모달이 사라진 것 자체는 성공이 아니다. **값이 들어왔을 때만** 성공이다 —
  // 안 그러면 팝업이 열리지 않는 화면에서 빈 칸을 채웠다고 보고하게 된다.
  const gone = () => !popupRoot(field);

  const waitRows = async (): Promise<HTMLElement[]> => {
    const rowsDeadline = Date.now() + SEARCH_MS;
    let found = rowsOf(root as HTMLElement, query);
    while (!found.length && Date.now() < rowsDeadline) {
      if (done() || gone()) return [];
      await sleep(POLL_MS);
      found = rowsOf(root as HTMLElement, query);
    }
    return found;
  };

  /** 지금 떠 있는 행들의 지문. 갱신됐는지는 개수가 아니라 내용으로 잰다. */
  const rowsSignature = (): string => rowsOf(root as HTMLElement, query).map(textOf).join('|');

  /**
   * 지문을 재기 전에 목록을 맨 위로 되감는다.
   *
   * 이 표는 가상 스크롤이라 **보이는 행만 DOM 에 있다.** 앞선 훑기가 목록을 바닥에 두고
   * 왔으면 지문도 바닥의 창에서 재게 되고, 그러면 앱이 새 결과를 받으며 스크롤을 위로
   * 되돌리는 것만으로 지문이 갈린다 — **아직 거르지도 않았는데** 갈린 것이라 기다림이
   * 거짓으로 끝나고 낡은 목록을 그대로 훑는다. 목록이 한 창을 넘는 화면에서 결함이
   * 관측된 자리가 정확히 여기다.
   *
   * 두 지문을 **같은 자리에서** 재면 그 착시가 없어진다. 이미 맨 위면 아무것도 안 한다.
   */
  const rewind = async (): Promise<void> => {
    const scroller = scrollerOf(root as HTMLElement);
    if (!scroller || !scroller.scrollTop) return;
    scroller.scrollTop = 0;
    scroller.dispatchEvent(new Event('scroll', { bubbles: true }));
    await sleep(POLL_MS);
  };

  const search = async (text: string): Promise<HTMLElement[]> => {
    if (!query) return waitRows();
    await rewind();
    const before = rowsSignature();
    await setValue(query, text);
    query.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true,
    } as KeyboardEventInit));
    // **갱신을 기다린다.** 치자마자 읽으면 이전 목록을 그대로 훑는다 — 같은 발화가
    // 판마다 다른 결과를 내던 자리다. 안 바뀌면(이미 그 목록이면) 시한까지만 기다리고 넘어간다.
    const settleBy = Date.now() + REFRESH_MS;
    while (rowsSignature() === before && Date.now() < settleBy) {
      if (done() || gone()) return [];
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
    return waitRows();
  };

  // 이름을 통째로 넣으면 0건이 흔하다 — 화면 표기와 저장된 이름이 조금씩 다르다
  // (`광주광산경찰서` 는 0건, `광주` 는 20건이었다). 전체 → 앞부분 → 빈 검색으로 좁힌다.
  // 빈 검색으로는 내려가지 않는다. 거르지 않은 목록에서는 무엇이 맞는지 알 수 없고,
  // 그 목록의 첫 행이 확정돼 엉뚱한 현장이 들어간 것을 봤다(`00001 1번입니다`).
  // 먼저 아무것도 치지 않고 뜬 목록에서 고른다. 이 화면들의 검색칸은 코드로만 거른다 —
  // 이름을 넣으면 목록이 통째로 비어 고를 것이 없어진다. 민원접수의 현장 팝업은 스물두 건이
  // 전부 떠 있었는데 `덕봉빌딩` 을 치는 순간 0건이 됐다. 이름이 정확히 같은 행이 있으면
  // 거기서 끝난다. 좁히기는 목록이 안 뜨거나 확신이 안 설 때만 쓴다.
  await waitRows();
  if (done()) return [];
  let scan = await scanRows(root as HTMLElement, query, (found) => pickRow(found, wanted));
  let rows: HTMLElement[] = scan.seen;
  let chosen = scan.chosen;
  const attempts = chosen ? [] : queriesFor(wanted);
  for (const attempt of attempts) {
    // eslint-disable-next-line no-await-in-loop
    const searched = await search(attempt);
    if (done()) return names(rows);
    if (!searched.length) continue;
    // eslint-disable-next-line no-await-in-loop
    scan = await scanRows(root as HTMLElement, query, (found) => pickRow(found, wanted));
    if (scan.seen.length) rows = scan.seen;
    chosen = scan.chosen;
    if (chosen) break;
  }
  if (done()) return names(rows);
  if (!chosen) {
    // 어느 검색어로도 0건이면 검색칸을 비우고 원래 목록을 훑는다. 그냥 실패로 끝내면
    // 사용자는 고를 기회조차 없다 — 초안을 못 채운 채 대화가 죽는다.
    if (!rows.length && query) {
      await setValue(query, '');
      query.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'Enter', keyCode: 13, which: 13, bubbles: true, cancelable: true,
      } as KeyboardEventInit));
      await waitRows();
      rows = (await scanRows(root as HTMLElement, query, (found) => pickRow(found, wanted))).seen;
      // 비우고 다시 부른 목록은 늦게 온다. 한 번은 더 기다린다 — 같은 현장이 어제는
      // 목록으로 떴고 오늘은 0건으로 끝났다. 사람 눈에는 둘 다 그냥 실패로 보인다.
      if (!rows.length) {
        await sleep(REFILL_MS);
        const again = await scanRows(root as HTMLElement, query, (found) => pickRow(found, wanted));
        rows = again.seen;
        if (again.chosen) {
          await chooseRow(again.chosen, root as HTMLElement);
          await sleep(POLL_MS);
          return names(rows);
        }
      }
    }
    closePopup(root as HTMLElement);
    if (!rows.length) throw new Error(`검색 팝업에서 ${wanted} 로 찾은 것이 없습니다.`);
    // 이름을 그대로 돌려준다. 사용자가 버튼으로 고르면 그 이름이 다음 시도의 값이 된다.
    // **닮은 것이 있으면 닮은 순으로 다섯 개만** 낸다 — 스무 개를 내밀면 사무직이 그
    // 목록에서 찾아야 하고, 고를 것이 정말 그 안에 없으면 이름을 다시 말하는 편이 빠르다.
    // 하나도 안 닮았을 때는 다르다. 아래에서 목록이 작을 때만, 그때는 **전부**(`names` 가
    // 내미는 상한 20까지) 내민다 — 다섯 개로 자르면 그건 목록이 아니라 조각이라
    // 사용자가 말한 적 없는 것을 고르게 된다(옛 코드는 여기서도 5개로 잘랐다).
    // 행을 잃지 않게 candidate 에서 라벨과 코드를 함께 뽑는다. 코드로 확정해야 하는
    // 표 셀 품목은 사용자가 고른 라벨을 코드로 바꿔 넣어야 되묻기 루프가 끝난다.
    const pairs = bestMatch(wanted, rows, (row) => labelOf(row, rows)).ranked
      .filter((entry) => entry.score > 0)
      .slice(0, 5)
      .map((entry) => ({ label: labelOf(entry.candidate, rows), code: codeOf(entry.candidate) }));
    if (pairs.length) {
      const err = new OptionNotFound(wanted, pairs.map((pair) => pair.label));
      err.optionCodes = pairs;
      throw err;
    }
    // 하나도 안 맞았다. **목록 전체를 보여줄 수 있을 때만** 후보로 내민다.
    // 작은 목록의 전부는 그 칸의 선택지 전부라 안 맞아도 사람이 고를 수 있다 —
    // `1호기` 라 말하는 호기 칸에 `1-1`~`1-3` 뿐인 경우가 그것이다. 반대로 수백 행짜리
    // 재고에서 앞 다섯 줄만 내밀면 그것은 사용자가 말한 적 없는 임의의 물건이다.
    // 실측: `자동통화장치` 를 물었는데 `WIRE ROPE` 가 후보로 나왔고, 현장 칸에는
    // 사람 이름 `조태호` 가 앉았다.
    if (rows.length <= WHOLE_LIST_MAX) throw new OptionNotFound(wanted, names(rows));
    const err = new OptionNotFound(wanted, []);
    // 이유가 없으면 런타임이 「못 읽었다」와 「읽었는데 없다」를 못 가른다.
    err.reason = '비슷한 이름을 찾지 못했다';
    throw err;
  }

  // 고르기는 누르는 순서를 다 따라간다. 표가 mousedown 에서 행을 잡는 화면이 있다.
  const live = await chooseRow(chosen, root as HTMLElement);
  await sleep(POLL_MS);

  // 어떤 팝업은 행을 고르는 것으로 끝나지 않고 `확인` 을 눌러야 확정한다.
  // 값이 아직 안 들어왔고 팝업이 그대로면 그 버튼을 누른다.
  if (!done() && !gone()) {
    const confirm = Array.from((root as HTMLElement).closest('[class*=ModalView]')
      ?.querySelectorAll<HTMLElement>('button') ?? [])
      .filter((button) => (button.textContent ?? '').trim() === '확인')
      .pop();
    // `확인` 은 **앱이 지금 포커스한 행**을 확정한다(`ModalCommon` 의 `selectedCommonData`).
    // 그 포커스는 우리 것이 아닐 수 있다 — 공용 검색 창은 목록을 다 받고 0.5초 뒤 첫 행에
    // 스스로 포커스하고(`GridLayout.setFocus(0)`), 그것이 우리 클릭을 덮어 비용항목에
    // `0101 직원급여` 가 들어갔다(2026-09-25). 누르기 직전에 고른 행을 한 번 더 누르고,
    // 그 행을 DOM 에서 못 찾으면 누르지 않는다 — 엉뚱한 행을 확정하느니 비워 두고
    // 부르는 쪽이 되묻게 한다.
    const target = await live();
    if (confirm && target.isConnected) {
      await sleep(ROW_FOCUS_MS);
      press(target, 'click');
      await sleep(ROW_SETTLE_MS);
      if (!done() && !gone()) {
        confirm.click();
        await sleep(POLL_MS);
      }
    }
  }
  // 훑으며 본 이름들을 돌려준다. 화면이 다른 행을 확정했을 때 부르는 쪽이 이것으로
  // 되물을 수 있다 — 팝업을 다시 여는 것은 방금 닫힌 자리에서 자주 씹힌다.
  return names(rows);
}

/**
 * 팝업을 닫는다.
 *
 * 못 고르고 나갈 때 열어 둔 채 두면 다음 턴에서 화면이 모달에 덮여 있다. 신규를
 * 눌러도 앱이 받지 않는다 — 실제로 되묻기에 답한 턴이 그렇게 죽었다.
 */
function closePopup(root: HTMLElement | null): void {
  if (!root) return;
  const close = Array.from(root.querySelectorAll<HTMLElement>('button,div,span,p'))
    .filter((element) => element.children.length === 0
      && ['닫기', '취소'].includes((element.textContent ?? '').trim()))
    .pop();
  if (close) { close.click(); return; }
  document.dispatchEvent(new KeyboardEvent('keydown', {
    key: 'Escape', keyCode: 27, which: 27, bubbles: true,
  } as KeyboardEventInit));
}

/** 지금 검색 모달이 떠 있는가. */
export function popupOpen(): boolean {
  return Boolean(popupRoot());
}

/**
 * 코드 칸과 짝이 되는 이름 칸의 값.
 *
 * 이 앱은 `actcd`/`actnm`, `perid`/`pernm`, `pcode`/`pname` 처럼 코드와 이름을 나란히
 * 둔다. 사람이 말하는 것은 이름이고 화면에 남는 것은 코드라, 이름 칸이 유일한 잣대다.
 */
export function pairedName(
  key: string,
  record: Record<string, unknown>,
  /** 화면에서 관측한 짝. `{ gregicd: 'greginm' }` 꼴이다. */
  pairs?: Record<string, string>,
): string | null {
  // **관측된 짝이 있으면 그것이 맞다.** 접미사 규칙(`cd`→`nm`)은 짐작이고,
  // 규약을 어기는 화면에서 조용히 틀린다 — `bildyd`/`buildnm` 처럼 어간이 다른 짝도 있다.
  const observed = pairs?.[key];
  if (observed && Object.prototype.hasOwnProperty.call(record, observed)) {
    return String(record[observed] ?? '');
  }
  const stem = key.replace(/(cd|id|code)$/i, '');
  if (stem === key) return null;
  for (const suffix of ['nm', 'name']) {
    const candidate = `${stem}${suffix}`;
    if (Object.prototype.hasOwnProperty.call(record, candidate)) {
      return String(record[candidate] ?? '');
    }
  }
  return null;
}

/** 두 이름이 같은 것을 가리키는가. */
/**
 * 화면이 확정한 이름과 사람이 말한 이름이 같은 것을 가리키는가.
 *
 * 이 회사의 현장 이름에는 소유자·지점이 괄호로 붙는다 — 사람은 `덕봉빌딩` 이라 말하고
 * 화면은 `덕봉빌딩(두암정철)` 을 넣는다. 글자가 정확히 같기를 요구했더니 팝업에서 **제대로
 * 고른 뒤에** 되물었고, 사용자는 방금 고른 것을 또 골라야 했다.
 *
 * 한쪽이 다른 쪽을 통째로 담고 있으면 같은 것으로 본다. 짧은 쪽이 두 글자 미만이면
 * 아무 데나 걸리므로 그때는 정확히 같아야 한다.
 */
export function sameName(left: string, right: string): boolean {
  const clean = (value: string) => value.replace(/[^0-9A-Za-z가-힣]/g, '').toLocaleLowerCase();
  const [one, other] = [clean(left), clean(right)];
  if (!one || !other) return false;
  if (one === other) return true;
  const [shorter, longer] = one.length <= other.length ? [one, other] : [other, one];
  return shorter.length >= 2 && longer.startsWith(shorter);
}

/**
 * 화면이 고른 것이 요청과 다르면, 후보를 모아 되묻게 던진다.
 *
 * 시한은 **부르는 쪽이 준다.** 이 자리는 이미 `pickFromSearchPopup` 이 자기 예산을 다
 * 쓰고 온 뒤라, 여기서 `OPEN_MS` 를 새로 박으면 칸 하나가 두 배를 먹는다.
 */
export async function requireChoice(
  anchor: HTMLElement,
  wanted: string,
  openMs = OPEN_MS,
): Promise<never> {
  const field = inputOf(anchor);
  // 이 되묻기는 방금 팝업을 닫고 나온 자리에서 불린다. 닫히는 중에 던진 더블클릭은
  // 씹히고, 그러면 후보 없이 던져 사용자는 고를 것이 없는 질문을 받는다.
  // 한 번 더 열어 보고, 그동안 눌리는 순서도 고르기와 같게 맞춘다.
  const open = (): void => {
    if (!field) return;
    press(field, 'mousedown');
    press(field, 'mouseup');
    press(field, 'click');
    press(field, 'dblclick', 2);
  };
  const root = await openRoot(field, open, openMs);
  // 안 열렸으면 화면의 표를 후보로 읽지 않는다. 빈 후보에 **이유**를 달아
  // 부르는 쪽이 「대안 없음」과 「못 열었다」를 가를 수 있게 한다.
  if (!root) throw popupNeverOpened(wanted);
  // 목록은 가상 스크롤이라 보이는 것만 DOM 에 있다. 끝까지 훑어 모은 뒤,
  // 사용자가 말한 것과 글자가 겹치는 것을 앞에 둔다 — `광주현장` 을 물었는데
  // `덕봉빌딩` 이 먼저 나오면 고를 수가 없다.
  const query = root.querySelector<HTMLInputElement>('input');
  const scanned = await scanRows(root, query, () => undefined);
  // 검색어보다 느슨하게 본다. `광주현장` 은 통째로는 아무 데도 안 걸리지만 `광주` 는 걸린다.
  // 순서만 바뀌는 자리라 짧은 조각을 써도 엉뚱한 값이 들어갈 위험은 없다.
  const bare = normalized(wanted);
  const tokens = [...queriesFor(wanted).map(normalized), bare.slice(0, 2), bare.slice(0, 3)]
    .filter((token) => token.length >= 2);
  const rows = scanned.seen.slice().sort((left, right) => {
    const score = (row: HTMLElement): number => (
      tokens.some((token) => normalized(nameOf(row)).includes(token)) ? 0 : 1
    );
    return score(left) - score(right);
  });
  closePopup(root);
  throw new OptionNotFound(wanted, names(rows));
}

/** 이 칸은 검색 팝업으로만 채울 수 있는가. */
export function hasSearchPopup(anchor: HTMLElement): boolean {
  const field = inputOf(anchor);
  return Boolean(field?.getAttribute('data-el-search'));
}
